import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RowType } from '../../common/constants';
import { selector as groupApi } from './groupSlice';

const useDeviceTable = () => {
    const deviceList = useSelector((state: any) => state.deviceApi.devices?.values);
    const groupList = useSelector(groupApi);
    const groupIdToHierarchyMap: Record<string, string> = groupList.groupIdToHierarchyMap;

    const deviceWithAllGroups = useMemo(
        () => Object.keys(groupIdToHierarchyMap)?.map((groupId) => {
            const parentHierarchyDetails = {
                groupId,
                parentHierarchy: groupIdToHierarchyMap[groupId],
                rowType: RowType.GROUP,
            };
            const groupHierarchy = groupList.groups.find(g => g.orgId === groupId).hierarchy;
            const devicesForGroup = deviceList?.filter((device) => 
                device?.organizations.some((org) => org === groupId)
              );
            
            const subRows = devicesForGroup?.map((device) => ({
                deviceId: device?.deviceId,
                deviceDisplayName: device?.displayName,
                deviceStatus: device?.deviceStatus,
                firmwareVersion: device?.firmwareVersion,
                iotAgentVersion: device?.iotAgentVersion,
                rowType: RowType.DEVICE,
                parentHierarchy: groupIdToHierarchyMap[groupId],
                organizations: device?.organizations,
                applicationVersion: device?.applicationVersion,
                androidVersion: device?.androidVersion,
                deviceType: device?.deviceType
            }));

            return {
                parentHierarchyDetails,
                subRows: subRows || [],
                groupHierarchy: groupHierarchy,
            };
        }),
        [groupIdToHierarchyMap, deviceList]);

    const deviceWithGroups = useMemo(
        () =>
            deviceList?.reduce((result, device) => {
                for (let org of device?.organizations) {
                    const groupId: string = org;
                    const groupPath = groupIdToHierarchyMap[groupId];
                    const parentHierarchyDetails = groupPath
                        ? { groupId, parentHierarchy: groupPath, rowType: RowType.GROUP }
                        : { groupId: '', parentHierarchy: '', lable: '' };

                    const existingEntry = result.find(
                        (entry) => entry.parentHierarchyDetails?.parentHierarchy === parentHierarchyDetails.parentHierarchy
                    );
                    const groupHierarchy = groupList.groups.find(g => g.orgId === groupId)?.hierarchy;
                    if (existingEntry) {
                        existingEntry.subRows.push({
                            deviceId: device?.deviceId,
                            deviceDisplayName: device?.displayName,
                            deviceStatus: device?.deviceStatus,
                            rowType: RowType.DEVICE,
                            parentHierarchy: groupIdToHierarchyMap[groupId],
                            applicationVersion: device?.applicationVersion,
                            androidVersion: device?.androidVersion,
                            firmwareVersion: device?.firmwareVersion,
                            iotAgentVersion: device?.iotAgentVersion,
                        });
                    } else {
                        result.push({
                            parentHierarchyDetails,
                            subRows: [
                                {
                                    deviceId: device?.deviceId,
                                    deviceDisplayName: device?.displayName,
                                    deviceStatus: device?.deviceStatus,
                                    rowType: RowType.DEVICE,
                                    parentHierarchy: groupIdToHierarchyMap[groupId],
                                    applicationVersion: device?.applicationVersion,
                                    androidVersion: device?.androidVersion,
                                    firmwareVersion: device?.firmwareVersion,
                                    iotAgentVersion: device?.iotAgentVersion,
                                },
                            ],
                            groupHierarchy: groupHierarchy,
                        });
                    }
                }
                return result;
            }, []) || [],
        [groupIdToHierarchyMap, deviceList]
    );

    return {
        deviceWithAllGroups,
        deviceWithGroups,
    };
};

export default useDeviceTable;
